import { z } from 'zod';

import { AssistantHandoffToolMetadataSchema } from './assistantHandoffTool.js';
import { VoiceCallTransferCallToolDestinationSchema } from './transferTool.js';
import { type BaseItem } from '../../common.js';
import { END_CALL_MESSAGE_MAX_LENGTH } from '../assistantDefinition/constants.js';

export * from './assistantHandoffTool.js';
export * from './transferTool.js';

export enum AssistantToolType {
  authenticateInboundPatient = 'authenticateInboundPatient',
  authenticateKnownPatient = 'authenticateKnownPatient',
  bookSelectedSlot = 'bookSelectedSlot',
  getAppointments = 'getAppointments',
  /**
   * @deprecated - use getEncounters instead
   */
  getEncounterDetails = 'getEncounterDetails',
  getEncounters = 'getEncounters',
  getOrders = 'getOrders',
  getPatient = 'getPatient',
  getPracticeDetails = 'getPracticeDetails',
  lookupPatient = 'lookupPatient',
  searchForOpenSlots = 'searchForOpenSlots',
  sendInboundAuthCode = 'sendInboundAuthCode',
  sendSms = 'sendSms',
  traverseDecisionTree = 'traverseDecisionTree',
  EndCall = 'EndCall',
  TransferCall = 'TransferCall',
  AssistantHandoff = 'AssistantHandoff',
}

// With no metadata requirement
export const GenericToolTypes = [
  AssistantToolType.authenticateInboundPatient,
  AssistantToolType.authenticateKnownPatient,
  AssistantToolType.bookSelectedSlot,
  AssistantToolType.getAppointments,
  AssistantToolType.getEncounters,
  AssistantToolType.getOrders,
  AssistantToolType.getPatient,
  AssistantToolType.getPracticeDetails,
  AssistantToolType.lookupPatient,
  AssistantToolType.searchForOpenSlots,
  AssistantToolType.sendInboundAuthCode,
  AssistantToolType.sendSms,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  AssistantToolType.getEncounterDetails,
] as const;

// Can be configured in the tool tab than a special UI
export const ToolTabConfigurableToolTypes = [
  ...GenericToolTypes,
  AssistantToolType.traverseDecisionTree,
] as const;

export const BaseGenericToolDefinitionSchema = z.object({
  id: z.string().uuid().optional(),
  type: z.nativeEnum(AssistantToolType),
  name: z.string(),
  organizationId: z.string().uuid(),
  description: z.string().nullable(),
  parameters: z.unknown(),
  startMessage: z.string().nullable(),
  failedMessage: z.string().nullable(),
  delayedMessage: z.string().nullable(),
  delayThresholdMilliseconds: z.number().nullable(),
  completeMessage: z.string().nullable(),
  timeoutSeconds: z.number().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  endCallAfterCompleteMessageSpoken: z.boolean().nullable(),
  endCallAfterFailedMessageSpoken: z.boolean().nullable(),
});

export const NotableDefinedToolDefinitionUpsertValuesSchema = BaseGenericToolDefinitionSchema.pick({
  id: true,
  type: true,
  name: true,
  organizationId: true,
  startMessage: true,
  failedMessage: true,
  delayedMessage: true,
  completeMessage: true,
  delayThresholdMilliseconds: true,
  metadata: true,
  endCallAfterCompleteMessageSpoken: true,
  endCallAfterFailedMessageSpoken: true,
});

export const BaseToolTabConfigurableToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.enum(ToolTabConfigurableToolTypes),
});
export type BaseToolTabConfigurableToolDefinition = z.infer<
  typeof BaseToolTabConfigurableToolDefinitionSchema
>;
export type ToolTabConfigurableToolDefinition = BaseItem & BaseToolTabConfigurableToolDefinition;

const BaseGenericOnlyToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.enum(GenericToolTypes),
});

export const GenericToolDefinitionUpsertValuesSchema = BaseGenericOnlyToolDefinitionSchema.pick({
  id: true,
  type: true,
  name: true,
  organizationId: true,
  startMessage: true,
  failedMessage: true,
  delayedMessage: true,
  delayThresholdMilliseconds: true,
  completeMessage: true,
  metadata: true,
  endCallAfterCompleteMessageSpoken: true,
  endCallAfterFailedMessageSpoken: true,
});

const BaseDecisionTreeToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.literal(AssistantToolType.traverseDecisionTree),
  metadata: z.object({
    decisionTreeId: z.string().uuid(),
  }),
});
export type BaseDecisionTreeToolDefinition = z.infer<typeof BaseDecisionTreeToolDefinitionSchema>;
export const DecisionTreeToolDefinitionUpsertValuesSchema =
  BaseDecisionTreeToolDefinitionSchema.pick({
    id: true,
    type: true,
    name: true,
    organizationId: true,
    metadata: true,
    startMessage: true,
    failedMessage: true,
    delayedMessage: true,
    delayThresholdMilliseconds: true,
    completeMessage: true,
    endCallAfterCompleteMessageSpoken: true,
    endCallAfterFailedMessageSpoken: true,
  });
export type DecisionTreeToolDefinitionUpsertValues = z.infer<
  typeof DecisionTreeToolDefinitionUpsertValuesSchema
>;

export type ToolTabConfigurableToolDefinitionUpsertValues =
  | z.infer<typeof GenericToolDefinitionUpsertValuesSchema>
  | DecisionTreeToolDefinitionUpsertValues;

export const BaseTransferCallToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.literal(AssistantToolType.TransferCall),
  metadata: z.object({
    destinations: z.array(VoiceCallTransferCallToolDestinationSchema),
  }),
});
export const TransferCallToolDefinitionUpsertValuesSchema =
  BaseTransferCallToolDefinitionSchema.pick({
    id: true,
    type: true,
    name: true,
    description: true,
    organizationId: true,
    metadata: true,
  });
export type BaseTransferCallToolDefinition = z.infer<typeof BaseTransferCallToolDefinitionSchema>;
export type TransferCallToolDefinitionUpsertValues = z.infer<
  typeof TransferCallToolDefinitionUpsertValuesSchema
>;

export const BaseAssistantHandoffToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.literal(AssistantToolType.AssistantHandoff),
  metadata: AssistantHandoffToolMetadataSchema,
});
export const AssistantHandoffToolDefinitionUpsertValuesSchema =
  BaseAssistantHandoffToolDefinitionSchema.pick({
    id: true,
    type: true,
    name: true,
    organizationId: true,
    metadata: true,
    startMessage: true,
    failedMessage: true,
    delayedMessage: true,
    delayThresholdMilliseconds: true,
    completeMessage: true,
    endCallAfterCompleteMessageSpoken: true,
    endCallAfterFailedMessageSpoken: true,
  });
export type BaseAssistantHandoffToolDefinition = z.infer<
  typeof BaseAssistantHandoffToolDefinitionSchema
>;
export type AssistantHandoffToolDefinitionUpsertValues = z.infer<
  typeof AssistantHandoffToolDefinitionUpsertValuesSchema
>;

export const BaseEndCallToolDefinitionSchema = BaseGenericToolDefinitionSchema.extend({
  type: z.literal(AssistantToolType.EndCall),
  startMessage: z.string().max(END_CALL_MESSAGE_MAX_LENGTH).nullable(),
});
export const EndCallToolDefinitionUpsertValuesSchema = BaseEndCallToolDefinitionSchema.pick({
  id: true,
  type: true,
  name: true,
  organizationId: true,
  startMessage: true,
});
export type BaseEndCallToolDefinition = z.infer<typeof BaseEndCallToolDefinitionSchema>;
export type EndCallToolDefinitionUpsertValues = z.infer<
  typeof EndCallToolDefinitionUpsertValuesSchema
>;

export const ToolDefinitionUpsertValuesSchema = z.discriminatedUnion('type', [
  GenericToolDefinitionUpsertValuesSchema,
  TransferCallToolDefinitionUpsertValuesSchema,
  AssistantHandoffToolDefinitionUpsertValuesSchema,
  EndCallToolDefinitionUpsertValuesSchema,
  DecisionTreeToolDefinitionUpsertValuesSchema,
]);

export type ToolDefinitionUpsertValues = z.infer<typeof ToolDefinitionUpsertValuesSchema>;

export const BaseToolDefinitionSchema = z.discriminatedUnion('type', [
  BaseTransferCallToolDefinitionSchema,
  BaseAssistantHandoffToolDefinitionSchema,
  BaseEndCallToolDefinitionSchema,
  BaseGenericOnlyToolDefinitionSchema,
]);
export type BaseToolDefinition = z.infer<typeof BaseToolDefinitionSchema>;
export type ToolDefinition = BaseItem & BaseToolDefinition;

export const ToolDefinitionFindAllOptionsSchema = z.object({
  organizationIds: z.array(z.string().uuid()).optional(),
  assistantDefinitionId: z.string().uuid().optional(),
  type: z.nativeEnum(AssistantToolType).optional(),
  name: z.string().optional(),
  description: z.string().optional(),
});
export type ToolDefinitionFindAllOptions = z.infer<typeof ToolDefinitionFindAllOptionsSchema>;

export function isToolTabConfigurableTools(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is ToolTabConfigurableToolDefinition {
  return Object.values(ToolTabConfigurableToolTypes).some((type) => type === toolDefinition.type);
}

export function isDecisionTreeToolDefinition(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is BaseDecisionTreeToolDefinition {
  return toolDefinition.type === AssistantToolType.traverseDecisionTree;
}

export function isTransferCallToolDefinition(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is BaseTransferCallToolDefinition {
  return toolDefinition.type === AssistantToolType.TransferCall;
}

export function isAssistantHandoffToolDefinition(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is BaseAssistantHandoffToolDefinition {
  return toolDefinition.type === AssistantToolType.AssistantHandoff;
}

export function isEndCallToolDefinition(toolDefinition: {
  type: AssistantToolType;
}): toolDefinition is BaseEndCallToolDefinition {
  return toolDefinition.type === AssistantToolType.EndCall;
}
